import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FaHome, 
  FaBook, 
  FaChartLine, 
  FaUserPlus, 
  FaPlus,
  FaSignOutAlt,
  FaUser,
  FaUsers,
  FaCog,
  FaChevronDown,
  FaBars,
  FaTimes
} from 'react-icons/fa';

const Navbar = () => {
  const { user, userType, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getMenuItems = () => {
    const baseItems = [
      { 
        path: '/dashboard', 
        label: 'Dashboard', 
        icon: FaHome 
      }
    ];

    switch (userType) {
      case 'teacher':
        return [
          ...baseItems,
          { 
            path: '/create-student', 
            label: 'Cadastrar Aluno', 
            icon: FaUserPlus 
          },
          { 
            path: '/students', 
            label: 'Meus Alunos', 
            icon: FaUsers 
          },
          { 
            path: '/create-homework', 
            label: 'Criar Lição', 
            icon: FaPlus 
          },
          { 
            path: '/homeworks', 
            label: 'Lições', 
            icon: FaBook 
          }
        ];
      case 'student':
        return [
          ...baseItems,
          { 
            path: '/homeworks', 
            label: 'Minhas Lições', 
            icon: FaBook 
          }
        ];
      case 'guardian':
        return [
          ...baseItems,
          { 
            path: '/homeworks', 
            label: 'Lições do Filho', 
            icon: FaBook 
          },
          { 
            path: '/analytics', 
            label: 'Relatórios', 
            icon: FaChartLine 
          }
        ];
      default:
        return baseItems;
    }
  };

  const getUserTypeLabel = () => {
    switch (userType) {
      case 'teacher':
        return 'Professor';
      case 'student':
        return 'Aluno';
      case 'guardian':
        return 'Responsável';
      default:
        return 'Usuário';
    }
  };

  const handleMobileMenuItemClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg fixed w-full top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/dashboard" className="text-xl font-bold text-blue-600 hover:text-blue-800">
              hmwkStudio
            </Link>
          </div>

          {/* Menu Desktop - escondido no mobile */}
          <div className="hidden md:flex items-center space-x-4">
            {getMenuItems().map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>

          <div className="flex items-center space-x-4">
            {/* Dropdown do usuário - Desktop */}
            <div className="hidden md:block relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
              <FaUser className="w-4 h-4" />
                <span className="hidden lg:inline">{user?.name} ({getUserTypeLabel()})</span>
                <span className="lg:hidden">{user?.name}</span>
                <FaChevronDown className={`w-3 h-3 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Menu Dropdown - Desktop */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  {userType === 'teacher' && (
                    <Link
                      to="/settings"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors"
                    >
                      <FaCog className="w-4 h-4" />
                      <span>Configurações</span>
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full text-left flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    <FaSignOutAlt className="w-4 h-4" />
                    <span>Sair</span>
                  </button>
                </div>
              )}
            </div>

            {/* Botão Hamburger - Mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {mobileMenuOpen ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Menu Mobile - Dropdown */}
      {mobileMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* Itens do Menu Mobile */}
            {getMenuItems().map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleMobileMenuItemClick}
                className="flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
            
            {/* Separador */}
            <hr className="my-2 border-gray-200" />
            
            {/* Informações do Usuário Mobile */}
            <div className="px-3 py-2">
              <div className="flex items-center space-x-3 mb-3">
                <FaUser className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">{getUserTypeLabel()}</p>
                </div>
              </div>
              
              {/* Configurações - Mobile */}
              {userType === 'teacher' && (
                <Link
                  to="/settings"
                  onClick={handleMobileMenuItemClick}
                  className="flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                >
                  <FaCog className="w-5 h-5" />
                  <span>Configurações</span>
                </Link>
              )}
              
              {/* Logout - Mobile */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <FaSignOutAlt className="w-5 h-5" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 